<template>
  <div>
    <modal v-if="addColorFormVisible" title="Ajout d'une couleur" @close="hideAddColorForm">
      <color-form :value="addColorFormVisible" title="Ajout d'une couleur" @submit="addColor">
        <template slot="actions">
          <form-button kind="submit" type="submit">Enregistrer</form-button>
          <form-button kind="cancel" @click="hideAddColorForm">Annuler</form-button>
        </template>
      </color-form>
    </modal>

    <modal v-if="colorEdited" title="Édition d'une couleur" @close="hideEditColorForm">
      <color-form :value="colorEdited" title="Édition d'une couleur" @submit="updateColor">
        <template slot="actions">
          <form-button kind="submit" type="submit">Enregistrer</form-button>
          <form-button kind="cancel" @click="hideEditColorForm">Annuler</form-button>
        </template>
      </color-form>
    </modal>

    <panel light>
      <h5 slot="heading" class="panel-title">{{pageTitle}}</h5>
      <div slot="heading" class="heading-elements">
        <div class="heading-buttons">
          <button class="btn btn-success" type="button" @click="showAddColorForm">Ajouter une couleur</button>
        </div>
      </div>
      <div slot="body" class="form-inline">
        <form-group>
          <form-input-search :value="filter.search" debounce @input="setFilterSearch"></form-input-search>
        </form-group>
        <form-group>
          <form-select :value="filter.group" @input="setFilterGroup" :options="colorGroups" placeholder="Filtrer par groupe"></form-select>
        </form-group>
        <form-group>
          <button v-if="isFiltered" type="button" class="btn btn-default" @click="resetFilter()">Effacer les filtres</button>
        </form-group>
      </div>

      <div slot="body">
        <hr />
        <row v-if="colors.length">
          <column lg="4" md="6" v-for="color in colors" :key="color.id">
            <panel>
              <ul is="table-row-actions" class="pull-right">
                <li><a @click="editColor(color)"><i class="icon-pencil7"></i> Modifier</a></li>
                <li class="divider"></li>
                <li><a @click="deleteColor(color)"><i class="icon-bin"></i> Supprimer</a></li>
              </ul>
              <div>
                <figure class="thumbnail pull-left no-margin mr-10">
                  <a @click.prevent="editColor(color)"><color-badge :color="color" /></a>
                </figure>
                <h6 class="no-margin text-semibold">
				  <a @click="editColor(color)">{{color.name}}</a>
                </h6>
                <p class="no-margin text-muted">{{color.group.name || '(aucun groupe)'}}</p>
              </div>
            </panel>
          </column>
        </row>
        <div v-else-if="$apollo.loading">
          <h4>Chargement&hellip;</h4>
        </div>
        <div v-else>
          <h4>Aucun résultat</h4>
          <button class="btn btn-success" type="button" @click="showAddColorForm">Ajouter une couleur</button>
        </div>
      </div>

      <pagination slot="footer" :total="results.totalCount" v-model="pagination"></pagination>
    </panel>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

import _map from "lodash/map";
import _some from 'lodash/some';

import FormInputSearch from "package-common-vuejs/src/components/form/FormInputSearch";
import Pagination from "package-common-vuejs/src/components/Pagination";
import TableRowActions from "package-common-vuejs/src/components/table/TableRowActions";
import Modal from "package-common-vuejs/src/components/Modal";
import ColorForm from "./ColorForm";
import ColorBadge from "../../components/ColorBadge";

import ColorsListQuery from "./graphql/ColorsListQuery.gql";
import ColorGroupsListQuery from "./graphql/ColorGroupsListQuery.gql";
import ColorRemoveMutation from "./graphql/ColorRemoveMutation.gql";
import ColorCreateMutation from "./graphql/ColorCreateMutation.gql";
import ColorUpdateMutation from "./graphql/ColorUpdateMutation.gql";

export default {
  components: {
    ColorForm,
    Modal,
    FormInputSearch,
    Pagination,
    TableRowActions,
    ColorBadge
  },
  data() {
    return {
      results: {
        totalCount: 0,
        edges: []
      },
      colorGroups: [],
      colorEdited: null,
      addColorFormVisible: null
    };
  },
  apollo: {
    results: {
      query: ColorsListQuery,
      fetchPolicy: "network-only",
      variables() {
        return {
          pagination: {
            after: this.pagination.offset,
            limit: this.pagination.limit
          },
          filter: {
            search: this.filter.search,
            group: this.filter.group === "---" ? "" : this.filter.group || null
          }
        };
      }
    },
    colorGroups: {
      query: ColorGroupsListQuery,
      fetchPolicy: "network-only",
      update: data =>
        _map(data.results, ({ name }) => ({
          value: name || "---",
          label: name || "(aucun groupe)"
        }))
    }
  },
  computed: {
    colors() {
      return _map(this.results.edges, "node");
    },
    filter() {
      return this.$store.state.colors.filter;
    },
    pagination: {
      get() {
        return this.$store.state.colors.pagination;
      },
      set(value) {
        this.setPagination(value);
      }
    },
    isFiltered() {
      return _some(this.filter);
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.refresh();
    });
  },
  methods: {
    ...mapActions('colors', [
      'setFilterSearch',
      'setFilterGroup',
      'setPagination',
      'resetFilter'
    ]),
    showAddColorForm() {
      this.addColorFormVisible = {
        name: this.filter.search,
        group: this.filter.group? {name: this.filter.group}: null,
      };
    },
    hideAddColorForm() {
      this.addColorFormVisible = false;
    },
    hideEditColorForm() {
      this.colorEdited = null;
    },
    refresh() {
      this.$apollo.queries.results.refetch();
      this.$apollo.queries.colorGroups.refetch();
    },
    async editColor(color) {
      this.colorEdited = color;
    },
    async deleteColor(color) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: ColorRemoveMutation,
          variables: {
            id: color.id
          }
        });
        if (data.result) {
          this.$store.dispatch(
            "addNotificationSuccess",
            `La couleur a été supprimée`
          );
          this.refresh();
        } else {
          this.$store.dispatch(
            "addNotificationError",
            `La couleur n'a pas pu être supprimée`
          );
        }
      } catch (err) {
        this.$store.dispatch("addNotificationError", err.message);
      }
    },
    async addColor(data) {
      try {
        await this.$apollo.mutate({
          mutation: ColorCreateMutation,
          variables: {
            data
          }
        });

        this.$store.dispatch(
          "addNotificationSuccess",
          `La couleur a été créée !`
        );
        this.hideAddColorForm();
        this.refresh();
      } catch (err) {
        this.$store.dispatch("addNotificationError", err.message);
      }
    },
    async updateColor(data) {
      try {
        await this.$apollo.mutate({
          mutation: ColorUpdateMutation,
          variables: {
            id: this.colorEdited.id,
            data
          }
        });
        this.$store.dispatch(
          "addNotificationSuccess",
          `La couleur a été mise à jour !`
        );
        this.hideEditColorForm();
        this.refresh();
      } catch (err) {
        this.$store.dispatch("addNotificationError", err.message);
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/pages/colors/Colors.vue